// NOW — the default opening state. Answers: where am I, what should I do,
// why, and which faculty helps. One Step, one action. Swipe the Step card
// sideways to walk the route (next/previous Step).
import React, { useRef } from 'react';
import { Animated, PanResponder, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import type { SelfState } from './useSelf';
import type { NowStep, NowPace } from './useCurrentStep';
import { GEAR_LABEL, SEAT_BY_ID } from './roster';
import { GlassCard, GoldButton, Kicker, Display, Medallion, OrderStrip } from './ui';
import { SECTOR_COLOR, SECTOR_NAME, makeStyles, useV10, type V10Palette } from './theme';

const driftColor = (t: V10Palette) => ({ 'on-pace': t.onPace, behind: t.behind, lapsed: t.lapsed }) as const;
const PACE_LABEL = { fresh: 'Fresh', steady: 'Steady', strained: 'Strained', drained: 'Drained' } as const;
const DRIFT_LABEL = { 'on-pace': 'On pace', behind: 'Behind pace', lapsed: 'Lapsed' } as const;

export function NowView({
  steps,
  pace,
  index,
  onIndex,
  self,
  loading,
  onTalk,
  onOpenTool,
  onCapture,
  onSetDirection,
}: {
  steps: NowStep[];
  pace?: NowPace | null;
  index: number;
  onIndex: (i: number) => void;
  self: SelfState;
  loading: boolean;
  onTalk: (seatId: string, step: NowStep | null) => void;
  onOpenTool: (step: NowStep) => void;
  onCapture: () => void;
  onSetDirection: () => void;
}) {
  const t = useV10();
  const styles = useStyles();
  const DRIFT_COLOR = driftColor(t);
  const step = steps[index] ?? null;
  const paceColor = pace ? (pace.state === 'fresh' ? t.goldBright : pace.state === 'steady' ? t.behind : t.lapsed) : t.behind;
  const dx = useRef(new Animated.Value(0)).current;
  const pan = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_e, g) => Math.abs(g.dx) > 14 && Math.abs(g.dx) > Math.abs(g.dy) * 1.4,
      onPanResponderMove: (_e, g) => dx.setValue(g.dx),
      onPanResponderRelease: (_e, g) => {
        const go = g.dx < -60 ? 1 : g.dx > 60 ? -1 : 0;
        Animated.spring(dx, { toValue: 0, useNativeDriver: true }).start();
        if (go) onIndexRef.current(go);
      },
    }),
  ).current;
  const onIndexRef = useRef((d: number) => {});
  onIndexRef.current = (d: number) => {
    const next = Math.min(steps.length - 1, Math.max(0, index + d));
    if (next !== index) onIndex(next);
  };

  if (!step) {
    return (
      <View style={styles.empty}>
        <Kicker>{loading ? 'Finding your place…' : 'You are clear'}</Kicker>
        <Display size={28} style={{ textAlign: 'center' }}>
          {loading ? '' : 'Nothing is pressing right now.'}
        </Display>
        {!loading ? (
          <>
            <Text style={styles.emptyBody}>Set a direction for a sector, or capture the next thing on your mind.</Text>
            <View style={styles.row}>
              <GoldButton label="Set direction" onPress={onSetDirection} />
              <GoldButton label="Capture" ghost onPress={onCapture} />
            </View>
          </>
        ) : null}
      </View>
    );
  }

  const seatView = self.byGear[step.gear];
  const seatId = seatView?.seat.id ?? step.tool;
  const seat = SEAT_BY_ID[seatId];
  const sectorColor = step.sector ? SECTOR_COLOR[step.sector] : t.gold;
  const crumbs = [
    step.sector ? SECTOR_NAME[step.sector] : null,
    step.trail.endeavour,
    step.trail.project,
    step.trail.aim,
    'Step',
  ].filter(Boolean) as string[];

  return (
    <ScrollView contentContainerStyle={styles.scroll} showsVerticalScrollIndicator={false}>
      <Kicker style={{ textAlign: 'center' }}>You are here</Kicker>
      <View style={styles.crumbs}>
        {crumbs.map((c, i) => (
          <React.Fragment key={`${c}-${i}`}>
            {i > 0 ? <Text style={styles.crumbSep}>›</Text> : null}
            <Text
              numberOfLines={1}
              style={[styles.crumb, i === 0 && step.sector ? { color: sectorColor } : null, i === crumbs.length - 1 && styles.crumbLast]}
            >
              {c}
            </Text>
          </React.Fragment>
        ))}
      </View>

      <Animated.View {...pan.panHandlers} style={{ transform: [{ translateX: dx }] }}>
        <GlassCard style={[styles.stepCard, { borderColor: step.urgent ? t.gold : t.navyLine }]}>
          <View style={styles.stepTop}>
            <Kicker color={step.sector ? sectorColor : t.goldBright}>
              {step.sector ? SECTOR_NAME[step.sector] : 'Today'} · {GEAR_LABEL[step.gear].name}
            </Kicker>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            {pace ? (
              <View style={[styles.driftPill, { borderColor: paceColor }]}>
                <View style={[styles.driftDot, { backgroundColor: paceColor }]} />
                <Text style={[styles.driftText, { color: paceColor }]}>
                  {PACE_LABEL[pace.state]} · C{pace.charge.toFixed(1)}
                </Text>
              </View>
            ) : null}
            {step.drift ? (
              <View style={[styles.driftPill, { borderColor: DRIFT_COLOR[step.drift] }]}>
                <View style={[styles.driftDot, { backgroundColor: DRIFT_COLOR[step.drift] }]} />
                <Text style={[styles.driftText, { color: DRIFT_COLOR[step.drift] }]}>{DRIFT_LABEL[step.drift]}</Text>
              </View>
            ) : null}
            </View>
          </View>
          <Display size={30} lines={3}>
            {step.title}
          </Display>
          <Text style={styles.when}>{step.when}</Text>
          <View style={styles.whyBox}>
            <Text style={styles.whyLabel}>WHY</Text>
            <Text style={styles.whyText}>{step.why}</Text>
          </View>
          {steps.length > 1 ? (
            <View style={styles.pager}>
              {steps.slice(0, 8).map((s, i) => (
                <Pressable key={s.key} onPress={() => onIndex(i)} hitSlop={6}>
                  <View style={[styles.pageDot, i === index && styles.pageDotOn]} />
                </Pressable>
              ))}
              <Text style={styles.pagerHint}>swipe for the route ahead</Text>
            </View>
          ) : null}
        </GlassCard>
      </Animated.View>

      <View style={styles.orderWrap}>
        <Kicker color={t.ivoryFaint} style={{ marginBottom: 10 }}>
          ORDER · {GEAR_LABEL[step.gear].question}
        </Kicker>
        <OrderStrip current={step.gear} />
      </View>

      {seat ? (
        <GlassCard style={styles.deityCard}>
          <Pressable style={styles.deityRow} onPress={() => onTalk(seat.id, step)}>
            <Medallion id={seat.id} size={74} glow state="active" />
            <View style={{ flex: 1, gap: 3 }}>
              <Kicker color={t.ivoryFaint}>Faculty for this step</Kicker>
              <Display size={22}>{seat.name}</Display>
              <Text style={styles.ability}>the ability to {seat.ability}</Text>
              {seatView ? (
                <View style={styles.levelRow}>
                  <View style={styles.levelTrack}>
                    <View style={[styles.levelFill, { width: `${Math.round(seatView.stats.pct * 100)}%` }]} />
                  </View>
                  <Text style={styles.levelText}>
                    L{seatView.stats.level} · {seatView.stats.points} pts
                  </Text>
                </View>
              ) : null}
            </View>
          </Pressable>
          <View style={styles.row}>
            <GoldButton label={`Talk to ${seat.name}`} onPress={() => onTalk(seat.id, step)} style={{ flex: 1 }} />
            <GoldButton label={`Open ${toolLabel(step.tool)}`} ghost onPress={() => onOpenTool(step)} style={{ flex: 1 }} />
          </View>
        </GlassCard>
      ) : null}

      {steps.length > 1 ? (
        <View style={styles.ahead}>
          <Kicker color={t.ivoryFaint}>Route ahead</Kicker>
          {steps
            .map((s, i) => ({ s, i }))
            .filter(({ i }) => i !== index)
            .slice(0, 3)
            .map(({ s, i }) => (
              <Pressable key={s.key} onPress={() => onIndex(i)} style={styles.aheadRow}>
                <View style={[styles.aheadDot, { backgroundColor: s.sector ? SECTOR_COLOR[s.sector] : t.gold }]} />
                <Text style={styles.aheadTitle} numberOfLines={1}>
                  {s.title}
                </Text>
                <Text style={styles.aheadWhen}>{s.when}</Text>
              </Pressable>
            ))}
        </View>
      ) : null}
    </ScrollView>
  );
}

function toolLabel(tool: string) {
  return (SEAT_BY_ID[tool]?.name ?? tool).toUpperCase();
}

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    scroll: { paddingHorizontal: 16, paddingTop: 10, paddingBottom: 40, gap: 14 },
    crumbs: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', gap: 6, marginTop: -4 },
    crumb: { color: t.ivorySoft, fontSize: 12, fontWeight: '600', maxWidth: 140 },
    crumbLast: { color: t.goldBright, fontWeight: '800' },
    crumbSep: { color: t.ivoryFaint, fontSize: 12 },
    stepCard: { gap: 10, paddingVertical: 20, backgroundColor: t.glassStrong },
    stepTop: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
    driftPill: { flexDirection: 'row', alignItems: 'center', gap: 6, borderWidth: 1, borderRadius: 999, paddingHorizontal: 8, paddingVertical: 3 },
    driftDot: { width: 7, height: 7, borderRadius: 4 },
    driftText: { fontSize: 11, fontWeight: '800' },
    when: { color: t.ivorySoft, fontSize: 14, fontWeight: '600' },
    whyBox: { borderLeftWidth: 2, borderLeftColor: t.gold, paddingLeft: 10, gap: 2 },
    whyLabel: { color: t.gold, fontSize: 10, fontWeight: '800', letterSpacing: 2 },
    whyText: { color: t.ivory, fontSize: 14.5, lineHeight: 20 },
    pager: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 4 },
    pageDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: t.dotOff },
    pageDotOn: { backgroundColor: t.goldBright, width: 16 },
    pagerHint: { color: t.ivoryFaint, fontSize: 11, marginLeft: 'auto' },
    orderWrap: { paddingHorizontal: 4 },
    deityCard: { gap: 14 },
    deityRow: { flexDirection: 'row', alignItems: 'center', gap: 14 },
    ability: { color: t.ivorySoft, fontSize: 13, fontStyle: 'italic' },
    levelRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 4 },
    levelTrack: { flex: 1, height: 4, borderRadius: 2, backgroundColor: t.track, overflow: 'hidden' },
    levelFill: { height: 4, backgroundColor: t.gold },
    levelText: { color: t.ivoryFaint, fontSize: 11, fontWeight: '700' },
    row: { flexDirection: 'row', gap: 10 },
    ahead: { gap: 8, paddingHorizontal: 4 },
    aheadRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: t.lineFaint },
    aheadDot: { width: 8, height: 8, borderRadius: 4 },
    aheadTitle: { flex: 1, color: t.ivory, fontSize: 14 },
    aheadWhen: { color: t.ivoryFaint, fontSize: 12 },
    empty: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 28, gap: 14 },
    emptyBody: { color: t.ivorySoft, textAlign: 'center', fontSize: 14, lineHeight: 20 },
  }),
);
